// ============================================================================
// latency.ts — per-leg latency data for the hardware-load comparison demo.
// ============================================================================
// ROADMAP D5 / PRD §1.9-B: "lead with the REAL per-leg latency we already
// collect, labeled by tier (≈$150 mini-PC → gaming rig)." Locked decision 4:
// NO paid cloud demo machines in beta — we show real collected numbers instead.
//
// >>> WIRE REAL METRICS HERE <<<
// The numbers below are REPRESENTATIVE PLACEHOLDERS seeded from the known
// shape of the pipeline (MODEL_STRATEGY.md: the fast ONNX gatekeeper is ~tens
// of ms/frame on CPU; the describer VLM is ~51s per FLAGGED frame on CPU and
// far faster on a GPU). Replace each `*_ms` with the ACTUAL per-leg timings the
// fleet already collects (the device records stage1_ms / stage2_ms per
// detection — pipeline.run_cascade). Capture a representative machine per tier
// and drop the real medians in. Keep this shape; the UI reads it directly.
//
// The four legs mirror the real pipeline path:
//   capture  — grab the screen frame (watcher cadence is separate; this is the
//              cost of one capture+preprocess).
//   gate     — Stage-1 ONNX gatekeeper score (runs on EVERY frame).
//   vlm      — Stage-2 describer VLM (runs ONLY on a flagged frame). Dominates.
//   upload   — moat note: for CUSTOMERS this is ~0 (nothing leaves the device);
//              shown here as the tenant-zero DEV-lane heartbeat/telemetry cost
//              so the comparison is honest about both lanes.
// ============================================================================

import type { Tier } from "./hardwareGrade";

export interface LatencyLeg {
  key: "capture" | "gate" | "vlm" | "upload";
  label: string;
  // Whether this leg runs on every frame or only on a flagged frame.
  cadence: "every-frame" | "flagged-only";
  ms: number;
}

export interface TierLatency {
  tier: Tier;
  // The representative machine this profile was captured on.
  machine: string;
  priceHint: string;
  legs: LatencyLeg[];
}

const leg = (
  key: LatencyLeg["key"],
  label: string,
  cadence: LatencyLeg["cadence"],
  ms: number,
): LatencyLeg => ({ key, label, cadence, ms });

// PLACEHOLDER representative numbers — replace with real collected medians.
export const TIER_LATENCY: TierLatency[] = [
  {
    tier: "lite",
    machine: "≈$150 mini-PC · CPU-only, 8 GB",
    priceHint: "≈ $150",
    legs: [
      leg("capture", "Screen capture", "every-frame", 18),
      leg("gate", "Fast gatekeeper", "every-frame", 95),
      leg("vlm", "Accurate describer (flagged frames)", "flagged-only", 51000),
      leg("upload", "Heartbeat / telemetry", "every-frame", 40),
    ],
  },
  {
    tier: "standard",
    machine: "Mainstream laptop · 6-core CPU + iGPU, 16 GB",
    priceHint: "≈ $600",
    legs: [
      leg("capture", "Screen capture", "every-frame", 12),
      leg("gate", "Fast gatekeeper", "every-frame", 45),
      leg("vlm", "Accurate describer (flagged frames)", "flagged-only", 9000),
      leg("upload", "Heartbeat / telemetry", "every-frame", 30),
    ],
  },
  {
    tier: "max",
    machine: "Gaming rig · RTX-class dGPU, 32 GB",
    priceHint: "≈ $1,800",
    legs: [
      leg("capture", "Screen capture", "every-frame", 8),
      leg("gate", "Fast gatekeeper", "every-frame", 14),
      leg("vlm", "Accurate describer (flagged frames)", "flagged-only", 1400),
      leg("upload", "Heartbeat / telemetry", "every-frame", 25),
    ],
  },
];

// Convenience: format a leg time as a human string (ms under 1s, else seconds).
export function fmtMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  return s >= 10 ? `${Math.round(s)} s` : `${s.toFixed(1)} s`;
}
